import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { Elements } from '@stripe/react-stripe-js'
import { Megaphone, ArrowRight, ExclamationMark } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import AddNewNumber from '../AddNewNumber/AddNewNumber'
import Loader from '../../../Loader/Loader'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import PaymentModal from '../../../Payment/PaymentModal/PaymentModal'
import { stripePromise } from '../utils'

import { useUpdateNumberSubscription, useCancelNumberSubscription } from './hooks'

const getFormattedTime = (date, timezone, format) => {
  if (!date) {
    return null
  }
  return formatInTimeZone(date, timezone, format)
}

const SelectedSystemNumber = ({
  createdAt,
  name,
  phone,
  phoneSubscriptionBillingDate,
  phoneSubscriptionStatus,
  timezone,
  buildingId,
  userRole,
  numberPackageToBuy,
  onClose,
}) => {
  const { t } = useTranslation(['chat'])
  const { updateNumberSubscription, isLoading: isUpdateLoading } = useUpdateNumberSubscription(
    buildingId,
    onClose,
  )

  const [cancelSubWindowVisibility, setCancelSubWindowVisibility] = useState(false)

  const hideCancelSubWindowVisibility = () => {
    setCancelSubWindowVisibility(false)
  }

  const { handleCancelSubscription, isLoading: isCancelLoading } = useCancelNumberSubscription(
    buildingId,
    onClose,
  )

  const [updateSubWindowVisibility, setUpdateSubWindowVisibility] = useState(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [areaCode, setAreaCode] = useState(null)

  const handleUpdateSubscription = async (body) => {
    const updatedBody = {
      ...body,
      areaCode: Number(areaCode),
      creditPackageId: numberPackageToBuy.id,
    }

    updateNumberSubscription(updatedBody)
  }

  return (
    <React.Fragment>
      <div className="navigation-container" onClick={onClose}>
        <ArrowRight size={24} />
        <span>{t('chat:selectedSystemNumber.back')}</span>
      </div>
      <div className="inbox__settings-selected-inbox-navbar">
        <div className="left-side">
          <div className="inbox-name-container">
            <div className="inbox-icon-container">
              <Megaphone size={24} />
            </div>
            <div>{name}</div>
          </div>
        </div>
        <div className="right-side">
          <div className="flex-col">
            <div className="field-name">{t('chat:selectedSystemNumber.phoneNumber')}</div>
            <div className="field-data">{phone || '-'}</div>
          </div>
          <div className="flex-col">
            <div className="field-name">{t('chat:selectedSystemNumber.createdAt')}</div>
            <div className="field-data">
              {createdAt ? formatInTimeZone(createdAt, timezone, 'd MMM, yyyy - h:mm a') : '-'}
            </div>
          </div>
        </div>
      </div>
      <div className="inbox__settings-selected-inbox-body">
        <div className="settings">
          <div className="left-side">
            <div className="section-header">{t('chat:selectedSystemNumber.inboxPhoneNumber')}</div>
            {isUpdateLoading || isCancelLoading ? (
              <div className="inbox__settings-loader">
                <Loader className="loader" />
              </div>
            ) : (
              <div className="flex-row">
                <div className="flex-col" style={{ width: 'fit-content' }}>
                  {phone &&
                  phoneSubscriptionBillingDate &&
                  phoneSubscriptionStatus &&
                  phoneSubscriptionStatus === 'canceled' ? (
                    <div className="subscription-info">
                      <span className="not-active">
                        <ExclamationMark size={24} />{' '}
                        {t('chat:selectedSystemNumber.subscriptionCancelledEndDate', {
                          date: getFormattedTime(
                            phoneSubscriptionBillingDate,
                            timezone,
                            'MM/dd/yyyy',
                          ),
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="standard-header">{t('chat:selectedInboxDetails.number')}</div>
                  )}
                  <div className="number-container">{phone || '-'}</div>
                </div>
                {!phone ? (
                  <button
                    className="button button-black is-block"
                    onClick={() => setUpdateSubWindowVisibility(true)}
                    disabled={isUpdateLoading}
                  >
                    {t('chat:selectedSystemNumber.buyANumber')}
                  </button>
                ) : phoneSubscriptionStatus && phoneSubscriptionStatus !== 'canceled' ? (
                  <button
                    className="button button-gray is-block"
                    onClick={() => setCancelSubWindowVisibility(true)}
                    disabled={!phone || isCancelLoading}
                  >
                    {t('chat:selectedSystemNumber.cancelSubscription')}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      {paymentModalVisible && (
        <Elements stripe={stripePromise}>
          <PaymentModal
            isOpen={paymentModalVisible}
            userRole={userRole}
            buildingId={buildingId}
            purchaseItemTitle="Phone number"
            purchaseItemPrice={numberPackageToBuy ? numberPackageToBuy.monthlyPrice : 0}
            handleSubmit={handleUpdateSubscription}
            handleClose={() => setPaymentModalVisible(false)}
          />
        </Elements>
      )}
      <AddNewNumber
        isOpen={updateSubWindowVisibility}
        handleClose={() => setUpdateSubWindowVisibility(false)}
        handleSubmit={(areaCode) => {
          setAreaCode(areaCode)
          setPaymentModalVisible(true)
        }}
      />
      <ConfirmPopup
        title={t('chat:inboxSettings.cancelSubscriptionConfirmTitle')}
        body={t('chat:inboxSettings.cancelSubscriptionBody', {
          endDate: getFormattedTime(phoneSubscriptionBillingDate, timezone, 'MM/dd/yyyy'),
        })}
        cancelText={t('common:actions.close')}
        confirmText={t('chat:selectedSystemNumber.cancelSubscription')}
        isOpen={cancelSubWindowVisibility}
        cancelCallback={hideCancelSubWindowVisibility}
        confirmCallback={handleCancelSubscription}
        className="is-warning"
      />
    </React.Fragment>
  )
}

SelectedSystemNumber.propTypes = {
  buildingId: PropTypes.number,
  createdAt: PropTypes.number,
  name: PropTypes.string,
  numberPackageToBuy: PropTypes.object,
  phone: PropTypes.number,
  phoneSubscriptionBillingDate: PropTypes.number,
  phoneSubscriptionStatus: PropTypes.string,
  timezone: PropTypes.string,
  userRole: PropTypes.string,
  onClose: PropTypes.func,
}

export default SelectedSystemNumber
